import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
   resetUnknownLocationWearables,
   wearableLocationUnknown,
} from "./unknownLocationWearablesSlice"
import {
   seedWearable,
   selectWearableRealtimeById,
   type WearableLocationInput,
} from "./wearablesRealtimeSlice"
import { RootState } from "."
import { ApiError, ApiResponse } from "@/api"
import { LevelInmateResponse, requestLevelInmates } from "@/api/InmatesService"
import { HttpCodes } from "@/constants/globals"
import { normalizeMacAddress } from "@/utils/index"

interface InmatesByLevelState {
   loading: boolean
   error: number | null
   currentLevelId: string | null
}

export const initialState: InmatesByLevelState = {
   loading: false,
   error: null,
   currentLevelId: null,
}

const resolveAnchorMacByLocationId = (
   locations: WearableLocationInput[]
): Map<string, string> => {
   const anchorMacByLocationId = new Map<string, string>()
   locations.forEach((location) => {
      const macAddress = location.sensors?.[0]?.macAddress
      if (macAddress) anchorMacByLocationId.set(location.id, macAddress)
   })
   return anchorMacByLocationId
}

const seedInmate = (
   inmate: LevelInmateResponse,
   anchorMacByLocationId: Map<string, string>,
   dispatch: (action: unknown) => void,
   getState: () => RootState
): void => {
   const macAddress = inmate.sensor?.macAddress
   if (!macAddress) return

   const wearableId = normalizeMacAddress(macAddress)
   const mappedInmate = {
      id: inmate.id,
      customIdentifier: inmate.customIdentifier,
      firstName: inmate.firstName,
      lastName: inmate.lastName,
   }

   const resolvedAnchorMac = inmate.location
      ? anchorMacByLocationId.get(inmate.location)
      : undefined

   if (resolvedAnchorMac) {
      dispatch(
         seedWearable({
            id: wearableId,
            anchorId: resolvedAnchorMac,
            inmate: mappedInmate,
         })
      )
      return
   }

   if (!selectWearableRealtimeById(getState(), wearableId)) {
      dispatch(
         wearableLocationUnknown({
            id: wearableId,
            macAddress,
            inmate: mappedInmate,
            timestamp: Date.now(),
         })
      )
   }
}

export const fetchInmatesByLevel = createAsyncThunk<
   ApiResponse<LevelInmateResponse[]>,
   { levelId: string; locations: WearableLocationInput[] },
   { state: RootState }
>(
   "inmatesByLevel/fetchInmatesByLevel",
   async ({ levelId, locations }, thunkAPI) => {
      const { dispatch, getState, rejectWithValue } = thunkAPI
      try {
         const response = await requestLevelInmates(levelId)

         if (getState().inmatesByLevelReducer.currentLevelId !== levelId) {
            return response
         }

         dispatch(resetUnknownLocationWearables())
         const anchorMacByLocationId = resolveAnchorMacByLocationId(locations)
         response.data.forEach((inmate) =>
            seedInmate(inmate, anchorMacByLocationId, dispatch, getState)
         )

         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return rejectWithValue(apiError.status)
         }
         return rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   },
   {
      condition: ({ levelId }, { getState }) => {
         const { loading, currentLevelId } = getState().inmatesByLevelReducer
         return !(loading && currentLevelId === levelId)
      },
   }
)

const inmatesByLevelSlice = createSlice({
   name: "inmatesByLevel",
   initialState,
   reducers: {},
   extraReducers: (builder) => {
      builder
         .addCase(fetchInmatesByLevel.pending, (state, action) => {
            state.loading = true
            state.currentLevelId = action.meta.arg.levelId
            state.error = null
         })
         .addCase(fetchInmatesByLevel.fulfilled, (state, action) => {
            if (action.meta.arg.levelId !== state.currentLevelId) return
            state.loading = false
         })
         .addCase(fetchInmatesByLevel.rejected, (state, action) => {
            if (action.meta.arg.levelId !== state.currentLevelId) return
            state.loading = false
            state.error = (action.payload as number) ?? null
         })
   },
})

export default inmatesByLevelSlice.reducer
